import { CaretDownIcon } from '@phosphor-icons/react';

import { DECISION_TEXT, INLINE_TEXT_HIT_AREA } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/**
 * WHAT THE PIPELINE STORED, read-only.
 *
 * `project_tasks.grounding` and `.anatomy` are what the Auto-order run based a
 * concept on. This is the disclosure that shows them without pretending they are
 * editable — they are provenance, and a marketer about to spend a credit on a
 * generation is exactly the person who needs "what is this visual direction
 * based on?" answerable in place.
 *
 * EXTRACTED, NOT REWRITTEN. This is `BriefConceptPage`'s own component, moved
 * out of that file unchanged so the Concept Workbench shows the same rows in the
 * same shape rather than growing a second renderer for the same JSON.
 */

function humanizeStoredKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

type StoredContextRow = {
  path: string[];
  value: string;
};

function storedLeafValue(value: unknown): string {
  if (value === null || value === undefined) return 'Not recorded';
  if (typeof value === 'string') return value.trim() || 'Not recorded';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return 'Structured context';
}

export function flattenStoredContext(
  value: unknown,
  emptyLabel: string,
  path: string[] = [],
  rows: StoredContextRow[] = [],
): StoredContextRow[] {
  if (value === null || value === undefined) {
    rows.push({ path: path.length ? path : ['State'], value: emptyLabel });
    return rows;
  }

  if (Array.isArray(value)) {
    if (value.length === 0)
      rows.push({ path: path.length ? path : ['Entries'], value: 'No entries' });
    value.forEach((item, index) => {
      flattenStoredContext(item, emptyLabel, [...path, `Item ${index + 1}`], rows);
    });
    return rows;
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0)
      rows.push({ path: path.length ? path : ['State'], value: emptyLabel });
    entries.forEach(([key, item]) => {
      flattenStoredContext(item, emptyLabel, [...path, humanizeStoredKey(key)], rows);
    });
    return rows;
  }

  rows.push({ path: path.length ? path : ['Recorded Value'], value: storedLeafValue(value) });
  return rows;
}

export function StoredContextDisclosure({
  title,
  value,
  emptyLabel,
}: {
  title: string;
  value: unknown;
  emptyLabel: string;
}): JSX.Element {
  const rows = flattenStoredContext(value, emptyLabel);

  return (
    <details className={cn('group', DECISION_TEXT)}>
      <summary
        className={cn(
          INLINE_TEXT_HIT_AREA,
          'flex cursor-pointer list-none items-center gap-2 text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden',
        )}
      >
        <CaretDownIcon className="size-3.5 transition-transform group-open:rotate-180" />
        <span className="text-foreground/85">{title}</span>
        <span className={cn(DECISION_TEXT, 'text-muted-foreground')}>Read stored details</span>
      </summary>
      <dl className="mt-3 flex flex-col gap-1.5 leading-relaxed text-muted-foreground">
        {rows.map((item, index) => (
          <div
            key={`${item.path.join('.')}-${index}`}
            className="grid grid-cols-[minmax(9rem,auto)_minmax(0,1fr)] gap-x-3 text-[13px]"
          >
            <dt className="text-foreground/75">{item.path.join(' · ')}</dt>
            <dd>{item.value}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}
